import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { Timestamp } from 'firebase/firestore';
import { 
  getEmployeesByUserFromShared, 
  getWorkSchedulesByUserFromShared,
  addEmployeeToShared,
  updateEmployeeInShared,
  deleteEmployeeFromShared,
  addWorkScheduleToShared,
  updateWorkScheduleInShared,
  deleteWorkScheduleFromShared,
  detachEmployeeFromSchedules,
  deleteWorkSchedulesByEmployee,
} from '../services/firestoreService';
import { Employee, WorkSchedule } from '../types/employee';
import {
  getSchedulesOnDate,
  hasExistingSchedule,
  buildCopiedScheduleData,
} from '../utils/scheduleCopy';
import { employeeHistoryService } from '../services/employeeHistoryService';
import { trackEvent } from '../services/analyticsService';
import { hourlyRateHistoryService } from '../services/hourlyRateHistoryService';
import { HourlyRateUpdateType } from '../types/hourlyRateHistory';

interface EmployeeState {
  // 상태
  employees: Employee[];
  schedules: WorkSchedule[];
  employeesLoading: boolean;
  schedulesLoading: boolean;
  loading: boolean; // 하위 호환성을 위해 유지
  error: string | null;
  lastFetchKey: string | null; // 중복 로딩 방지를 위한 키
  
  // 액션
  fetchEmployees: (userId: string, accountNumber?: number) => Promise<void>;
  fetchSchedules: (userId: string, selectedDate?: Date, accountNumber?: number) => Promise<void>;
  createEmployee: (userId: string, employee: Omit<Employee, 'id'>) => Promise<void>;
  updateEmployee: (userId: string, employeeId: string, updates: Partial<Employee>, options?: {
    updateType?: 'fullUpdate' | 'newOnly';
    reason?: string;
  }) => Promise<void>;
  deleteEmployee: (userId: string, employeeId: string) => Promise<void>;
  deleteRetiredEmployee: (
    userId: string,
    employeeId: string,
    options: { deleteWorkRecords: boolean; accountNumber: number },
  ) => Promise<void>;
  createSchedule: (userId: string, schedule: Omit<WorkSchedule, 'id'>) => Promise<void>;
  updateSchedule: (userId: string, scheduleId: string, updates: Partial<WorkSchedule>) => Promise<void>;
  deleteSchedule: (userId: string, scheduleId: string) => Promise<void>;
  copyDaySchedules: (
    userId: string,
    sourceDateStr: string,
    targetDateStrs: string[],
  ) => Promise<{ copied: number; skipped: number }>;

  // 선택자
  getEmployeeById: (employeeId: string) => Employee | undefined;
  getSchedulesByDate: (date: Date) => WorkSchedule[];
  getSchedulesByEmployee: (employeeId: string) => WorkSchedule[];
  
  // 상태 관리
  setEmployees: (employees: Employee[]) => void;
  setSchedules: (schedules: WorkSchedule[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearCache: () => void; // 캐시 초기화
}

export const useEmployeeStore = create<EmployeeState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // 초기 상태
      employees: [],
      schedules: [],
      employeesLoading: false,
      schedulesLoading: false,
      loading: false,
      error: null,
      lastFetchKey: null,

      // 직원 데이터 가져오기
      fetchEmployees: async (userId: string, accountNumber?: number) => {
        const fetchKey = `employees-${userId}-${accountNumber}`;
        
        // 중복 로딩 방지 - 이미 같은 데이터를 로딩 중이거나 로딩했으면 스킵
        const currentState = get();
        if (currentState.employeesLoading) {
          return;
        }
        
        // 이미 같은 키로 로드한 적이 있으면 스킵
        if (currentState.lastFetchKey === fetchKey && currentState.employees.length > 0) {
          return;
        }
        
        try {
          set({ employeesLoading: true, loading: true, error: null });
          
          
          // firestoreService를 사용하여 sharedBooks에서 직원 데이터 가져오기
          const employeesData = await getEmployeesByUserFromShared(userId, accountNumber);
          
          set({ 
            employees: employeesData, 
            employeesLoading: false, 
            loading: false,
            lastFetchKey: fetchKey
          });
        } catch (error: any) {
          console.error('❌ 직원 데이터 가져오기 실패:', error);
          set({ error: error.message, employeesLoading: false, loading: false });
        }
      },

      // 근무 일정 데이터 가져오기
      fetchSchedules: async (userId: string, selectedDate?: Date, accountNumber?: number) => {
        const dateKey = selectedDate ? selectedDate.toISOString().split('T')[0] : 'all';
        const fetchKey = `schedules-${userId}-${accountNumber}-${dateKey}`;
        
        // 중복 로딩 방지
        const currentState = get();
        if (currentState.schedulesLoading) {
          return;
        }
        
        // 이미 같은 키로 로드한 적이 있으면 스킵 (날짜가 바뀌면 다시 로드)
        if (currentState.lastFetchKey === fetchKey && currentState.schedules.length >= 0) {
          return;
        }
        
        try {
          set({ schedulesLoading: true, loading: true, error: null });
          
          
          // selectedDate 유효성 검사
          if (selectedDate) {
            const dateObj = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
            if (isNaN(dateObj.getTime())) {
              console.warn('유효하지 않은 날짜:', selectedDate);
              set({ schedulesLoading: false, loading: false });
              return;
            }
          }
          
          // firestoreService를 사용하여 sharedBooks에서 근무 일정 데이터 가져오기
          const schedulesData = await getWorkSchedulesByUserFromShared(userId, selectedDate, accountNumber);
          
          set({ 
            schedules: schedulesData, 
            schedulesLoading: false, 
            loading: false,
            lastFetchKey: fetchKey
          });
        } catch (error: any) {
          console.error('❌ 근무 일정 데이터 가져오기 실패:', error);
          set({ error: error.message, schedulesLoading: false, loading: false });
        }
      },

      // 직원 생성
      createEmployee: async (userId: string, employee: Omit<Employee, 'id'>) => {
        try {
          set({ loading: true, error: null });
          
          
          const employeeData = {
            ...employee,
            ownerId: userId,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          };
          
          const employeeId = await addEmployeeToShared(userId, employeeData);
          
          // 상태 업데이트
          const newEmployee = { ...employeeData, id: employeeId } as Employee;
          set(state => ({ 
            employees: [newEmployee, ...state.employees],
            loading: false 
          }));
          
          // 히스토리 기록 (비동기, 실패해도 메인 작업은 계속)
          employeeHistoryService.recordEmployeeCreated(userId, newEmployee, { skipFirestoreSync: false })
            .catch(err => console.error('⚠️ 직원 생성 히스토리 기록 실패:', err));

          // 이용 로그 — 개인정보(이름·급여액) 미포함, 급여 방식만 기록
          void trackEvent('employee_added', { is_hourly: !!employee.isHourly });
          
        } catch (error: any) {
          console.error('❌ 직원 생성 실패:', error);
          set({ error: error.message, loading: false });
        }
      },

      // 직원 수정
      updateEmployee: async (userId: string, employeeId: string, updates: Partial<Employee>, options?: {
        updateType?: 'fullUpdate' | 'newOnly';
        reason?: string;
      }) => {
        try {
          set({ loading: true, error: null });
          
          
          // 수정 전 데이터 가져오기 (히스토리 기록용)
          const currentState = get();
          const oldEmployee = currentState.employees.find(emp => emp.id === employeeId);
          
          // 시급 변경 여부 확인
          const isHourlyRateChanged = 
            oldEmployee &&
            oldEmployee.isHourly &&
            updates.hourlyRate !== undefined &&
            updates.hourlyRate !== oldEmployee.hourlyRate;
          
          
          const updateData = {
            ...updates,
            updatedAt: Timestamp.now()
          };
          
          await updateEmployeeInShared(userId, employeeId, updateData);
          
          // 상태 업데이트
          const updatedEmployee = currentState.employees.map(emp => 
            emp.id === employeeId ? { ...emp, ...updateData } : emp
          ).find(emp => emp.id === employeeId);
          
          set(state => ({
            employees: state.employees.map(emp => 
              emp.id === employeeId ? { ...emp, ...updateData } : emp
            ),
            loading: false
          }));
          
          // 히스토리 기록 (비동기, 실패해도 메인 작업은 계속)
          if (oldEmployee && updatedEmployee) {
            employeeHistoryService.recordEmployeeUpdated(userId, oldEmployee, updatedEmployee as Employee, { skipFirestoreSync: false })
              .catch(err => console.error('⚠️ 직원 수정 히스토리 기록 실패:', err));
            
            // 시급 변경 이력 저장 (비동기, 실패해도 메인 작업은 계속)
            if (isHourlyRateChanged) {
              
              hourlyRateHistoryService.saveHistory(userId, {
                employeeId,
                previousRate: oldEmployee.hourlyRate,
                newRate: updates.hourlyRate!,
                changedAt: new Date(),
                accountNumber: oldEmployee.account_number || oldEmployee.accountNumber || 1,
                reason: options?.reason,
                updateType: options?.updateType === 'newOnly' 
                  ? HourlyRateUpdateType.NEW_ONLY 
                  : HourlyRateUpdateType.FULL_UPDATE,
              })
              .catch(err => console.error('⚠️ 시급 변경 이력 기록 실패:', err));
            }
          }
          
        } catch (error: any) {
          console.error('❌ 직원 수정 실패:', error);
          set({ error: error.message, loading: false });
        }
      },

      // 퇴직자 삭제 — 근무 기록 유지/삭제 선택 (Flutter V53)
      deleteRetiredEmployee: async (
        userId: string,
        employeeId: string,
        options: { deleteWorkRecords: boolean; accountNumber: number },
      ) => {
        const currentState = get();
        const employee = currentState.employees.find((emp) => emp.id === employeeId);
        if (!employee) {
          throw new Error('직원을 찾을 수 없습니다.');
        }

        try {
          set({ loading: true, error: null });

          if (options.deleteWorkRecords) {
            await deleteWorkSchedulesByEmployee(userId, employeeId, options.accountNumber);
          } else {
            await detachEmployeeFromSchedules(
              userId,
              employeeId,
              options.accountNumber,
              employee.name,
            );
          }

          await deleteEmployeeFromShared(userId, employeeId);

          set((state) => {
            const nextSchedules = options.deleteWorkRecords
              ? state.schedules.filter((s) => s.employee_id !== employeeId)
              : state.schedules.map((s) =>
                  s.employee_id === employeeId
                    ? {
                        ...s,
                        employee_id: '',
                        employee_name: '',
                        snapshot_employee_name: employee.name,
                        snapshotEmployeeName: employee.name,
                      }
                    : s,
                );

            return {
              employees: state.employees.filter((emp) => emp.id !== employeeId),
              schedules: nextSchedules,
              loading: false,
            };
          });

          employeeHistoryService
            .recordEmployeeDeleted(userId, employee, { skipFirestoreSync: false })
            .catch((err) => console.error('⚠️ 직원 삭제 히스토리 기록 실패:', err));

        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : '퇴직자 정보 삭제 중 오류';
          console.error('[퇴직자삭제] 실패:', error);
          set({ error: message, loading: false });
          throw error;
        }
      },

      // 직원 삭제
      deleteEmployee: async (userId: string, employeeId: string) => {
        try {
          set({ loading: true, error: null });
          
          
          // 삭제 전 데이터 가져오기 (히스토리 기록용)
          const currentState = get();
          const employee = currentState.employees.find(emp => emp.id === employeeId);
          
          await deleteEmployeeFromShared(userId, employeeId);
          
          // 상태 업데이트
          set(state => ({
            employees: state.employees.filter(emp => emp.id !== employeeId),
            loading: false
          }));
          
          // 히스토리 기록 (비동기, 실패해도 메인 작업은 계속)
          if (employee) {
            employeeHistoryService.recordEmployeeDeleted(userId, employee, { skipFirestoreSync: false })
              .catch(err => console.error('⚠️ 직원 삭제 히스토리 기록 실패:', err));
          }
          
        } catch (error: any) {
          console.error('❌ 직원 삭제 실패:', error);
          set({ error: error.message, loading: false });
        }
      },

      // 근무 일정 생성
      createSchedule: async (userId: string, schedule: Omit<WorkSchedule, 'id'>) => {
        try {
          set({ loading: true, error: null });
          
          
          // date를 ISO 형식으로 변환 (YYYY-MM-DD → 2025-12-24T00:00:00.000Z)
          let dateStr = schedule.date;
          if (typeof dateStr === 'string' && !dateStr.includes('T')) {
            dateStr = `${dateStr}T00:00:00.000Z`;
          }
          
          // 중복 체크: 같은 날짜, 같은 직원, 같은 가계부 번호 조합이 이미 있는지 확인
          const currentState = get();
          const dateForComparison = typeof schedule.date === 'string' 
            ? (schedule.date.includes('T') ? schedule.date.split('T')[0] : schedule.date)
            : schedule.date;
          
          const existingSchedule = currentState.schedules.find(sched => {
            const schedDate = typeof sched.date === 'string' 
              ? (sched.date.includes('T') ? sched.date.split('T')[0] : sched.date)
              : sched.date;
            return sched.employee_id === schedule.employee_id &&
                   sched.account_number === schedule.account_number &&
                   schedDate === dateForComparison;
          });
          
          if (existingSchedule) {
            // 중복 스케줄: 에러를 throw하지 않고 알림만 표시하고 조기 종료
            set({ loading: false, error: null });
            const errorMessage = '해당 날짜에 이미 해당 직원의 스케줄이 존재합니다. 기존 스케줄을 수정해주세요.';
            alert(errorMessage);
            // Promise를 resolve하여 정상 종료 (에러를 throw하지 않음)
            return Promise.resolve();
          }
          
          // 템플릿 기반 스케줄인지 확인
          const isTemplateBased = !!(schedule.templateId || schedule.workTime1 || schedule.workTime2);
          
          let scheduleData: any = {
            date: dateStr,
            break_time: schedule.break_time || '1시간',
            account_number: schedule.account_number,
            accountNumber: schedule.account_number, // Flutter 앱 호환성: accountNumber도 저장
            is_favorite: false,
            chat_messages: [],
          };
          
          if (isTemplateBased) {
            // 템플릿 기반 스케줄
            scheduleData.templateId = schedule.templateId;
            scheduleData.workTime1 = schedule.workTime1;
            scheduleData.workTime2 = schedule.workTime2;
            scheduleData.hourlyRate = schedule.hourlyRate;
            
            // Flutter 앱 호환성: employee_id가 있으면 start_time, end_time, workTimes 맵도 생성
            if (schedule.employee_id) {
              const stateForEmployee = get();
              const employee = stateForEmployee.employees.find(emp => emp.id === schedule.employee_id);
              
              if (employee) {
                // 템플릿 기반 스케줄: start_time/end_time은 start_time_1/end_time_1과 동일하게 저장
                // workTime1의 시간 범위를 사용하여 start_time, end_time 생성 (앱 데이터와 일치)
                if (schedule.workTime1) {
                  // Date 객체를 ISO 문자열로 변환 (나중에 firestoreService에서 'Z' 제거됨)
                  scheduleData.start_time = schedule.workTime1.start instanceof Date 
                    ? schedule.workTime1.start.toISOString()
                    : new Date(schedule.workTime1.start).toISOString();
                  scheduleData.end_time = schedule.workTime1.end instanceof Date 
                    ? schedule.workTime1.end.toISOString()
                    : new Date(schedule.workTime1.end).toISOString();
                } else if (schedule.workTime2) {
                  scheduleData.start_time = schedule.workTime2.start instanceof Date 
                    ? schedule.workTime2.start.toISOString()
                    : new Date(schedule.workTime2.start).toISOString();
                  scheduleData.end_time = schedule.workTime2.end instanceof Date 
                    ? schedule.workTime2.end.toISOString()
                    : new Date(schedule.workTime2.end).toISOString();
                }
                
                // employee_id 및 직원 정보 추가
                scheduleData.employee_id = schedule.employee_id;
                scheduleData.employee_name = employee.name;
                
                // 직원 정보 중복 저장 (Flutter 앱 호환)
                scheduleData.mp = employee.mp;
                scheduleData.isHourly = employee.isHourly;
                scheduleData.monthlySalary = employee.monthlySalary;
                scheduleData.weeklyOffDays = employee.weeklyOffDays;
                scheduleData.weeklyWorkHours = employee.weeklyWorkHours;
                scheduleData.taxRate = employee.taxRate;
                scheduleData.dependents = employee.dependents;
                scheduleData.hasFourInsurance = employee.hasFourInsurance;
                scheduleData.payday = employee.payday;
                scheduleData.isweeklyWork = employee.isweeklyWork;
                scheduleData.hire_date = employee.hire_date;
                scheduleData.severance_pay = employee.severance_pay ?? 0; // Flutter 앱 호환성: null이면 0으로 저장
              }
            }
          } else {
            // 커스텀 스케줄 (기존 로직)
            const stateForEmployee = get();
            const employee = stateForEmployee.employees.find(emp => emp.id === schedule.employee_id);
            
            if (!employee) {
              throw new Error('직원을 찾을 수 없습니다.');
            }
            
            scheduleData.employee_id = schedule.employee_id;
            scheduleData.employee_name = employee.name;
            scheduleData.start_time = schedule.start_time;
            scheduleData.end_time = schedule.end_time;
            
            // Flutter 앱 호환성: 커스텀 스케줄도 start_time_1, end_time_1로 저장 (workTime1이 없는 경우)
            // toFirebaseMap에서 workTimes Map이 있고 workTime1이 없으면 workTimes의 첫 번째 값을 사용
            scheduleData.start_time_1 = schedule.start_time;
            scheduleData.end_time_1 = schedule.end_time;
            
            // 직원 정보 중복 저장 (Flutter 앱 호환)
            scheduleData.mp = employee.mp;
            scheduleData.isHourly = employee.isHourly;
            scheduleData.hourlyRate = employee.hourlyRate;
            scheduleData.monthlySalary = employee.monthlySalary;
            scheduleData.weeklyOffDays = employee.weeklyOffDays;
            scheduleData.weeklyWorkHours = employee.weeklyWorkHours;
            scheduleData.taxRate = employee.taxRate;
            scheduleData.dependents = employee.dependents;
            scheduleData.hasFourInsurance = employee.hasFourInsurance;
            scheduleData.payday = employee.payday;
            scheduleData.isweeklyWork = employee.isweeklyWork;
            scheduleData.hire_date = employee.hire_date;
            scheduleData.severance_pay = employee.severance_pay ?? 0; // Flutter 앱 호환성: null이면 0으로 저장
          }
          
          // UUID + 날짜 형식으로 스케줄 ID 생성
          // dateStr는 이미 위에서 선언되었으므로 재사용
          const uuid = crypto.randomUUID();
          const scheduleId = `${uuid}_${dateStr}`;
          scheduleData.id = scheduleId;
          
          await addWorkScheduleToShared(userId, scheduleId, scheduleData);
          
          // 실시간 리스너가 상태를 업데이트하므로 로컬 상태 업데이트는 제거
          // 로딩 상태만 업데이트
          set({ loading: false });
          
          // 히스토리 기록 (비동기, 실패해도 메인 작업은 계속)
          const newSchedule = { ...scheduleData, id: scheduleId } as WorkSchedule;
          employeeHistoryService.recordWorkScheduleCreated(userId, newSchedule, { skipFirestoreSync: false })
            .catch(err => console.error('⚠️ 근무 일정 생성 히스토리 기록 실패:', err));

          // 이용 로그 — 개인정보(직원 이름) 미포함
          void trackEvent('schedule_added', { template_based: isTemplateBased });
          
        } catch (error: any) {
          console.error('❌ 근무 일정 생성 실패:', error);
          set({ error: error.message, loading: false });
        }
      },

      // 하루치 스케줄을 여러 대상 날짜로 복사 (그날 전체 인력, 중복 직원은 skip)
      copyDaySchedules: async (
        userId: string,
        sourceDateStr: string,
        targetDateStrs: string[],
      ) => {
        const state = get();
        const sources = getSchedulesOnDate(state.schedules, sourceDateStr);
        let copied = 0;
        let skipped = 0;

        if (sources.length === 0 || targetDateStrs.length === 0) {
          return { copied, skipped };
        }

        set({ loading: true, error: null });
        try {
          // 이번 실행에서 이미 생성한 (직원|가계부|날짜) 조합 추적 (리스너 반영 전 자체 중복 방지)
          const createdKeys = new Set<string>();

          for (const targetDateStr of targetDateStrs) {
            // 원본 날짜로 복사하는 것은 무의미 → 스킵
            if (targetDateStr === sourceDateStr) continue;

            for (const source of sources) {
              const key = `${source.employee_id}|${source.account_number}|${targetDateStr}`;
              const isDup =
                createdKeys.has(key) ||
                hasExistingSchedule(
                  state.schedules,
                  source.employee_id,
                  source.account_number,
                  targetDateStr,
                );
              if (isDup) {
                skipped++;
                continue;
              }

              const scheduleData = buildCopiedScheduleData(source, targetDateStr);
              const dateStr = `${targetDateStr}T00:00:00.000Z`;
              scheduleData.date = dateStr;
              scheduleData.accountNumber = source.account_number; // Flutter 호환
              const scheduleId = `${crypto.randomUUID()}_${dateStr}`;
              scheduleData.id = scheduleId;

              await addWorkScheduleToShared(userId, scheduleId, scheduleData);
              createdKeys.add(key);
              copied++;

              // 히스토리 (비동기, 실패해도 메인 작업 계속)
              const newSchedule = { ...scheduleData } as WorkSchedule;
              employeeHistoryService
                .recordWorkScheduleCreated(userId, newSchedule, { skipFirestoreSync: false })
                .catch((err) => console.error('⚠️ 복사 일정 히스토리 기록 실패:', err));
            }
          }

          set({ loading: false });
          return { copied, skipped };
        } catch (error: any) {
          console.error('❌ 일정 복사 실패:', error);
          set({ error: error.message, loading: false });
          return { copied, skipped };
        }
      },

      // 근무 일정 수정
      updateSchedule: async (userId: string, scheduleId: string, updates: Partial<WorkSchedule>) => {
        try {
          set({ loading: true, error: null });
          
          
          // 직원 정보가 변경된 경우 전체 직원 정보 업데이트
          const currentState = get();
          let updateData: any = { ...updates, updatedAt: Timestamp.now() };
          
          // 템플릿 기반 스케줄인지 확인
          const isTemplateBased = !!(updates.templateId || updates.workTime1 || updates.workTime2);
          
          // 템플릿 기반 스케줄: start_time/end_time은 start_time_1/end_time_1과 동일하게 설정 (앱 데이터와 일치)
          if (isTemplateBased && updates.workTime1) {
            updateData.start_time = updates.workTime1.start instanceof Date 
              ? updates.workTime1.start.toISOString()
              : new Date(updates.workTime1.start).toISOString();
            updateData.end_time = updates.workTime1.end instanceof Date 
              ? updates.workTime1.end.toISOString()
              : new Date(updates.workTime1.end).toISOString();
          } else if (isTemplateBased && updates.workTime2) {
            updateData.start_time = updates.workTime2.start instanceof Date 
              ? updates.workTime2.start.toISOString()
              : new Date(updates.workTime2.start).toISOString();
            updateData.end_time = updates.workTime2.end instanceof Date 
              ? updates.workTime2.end.toISOString()
              : new Date(updates.workTime2.end).toISOString();
          } else if (updates.start_time && updates.end_time) {
            // 커스텀 스케줄: start_time_1, end_time_1도 설정 (Flutter 앱 호환성)
            const startTimeValue = updates.start_time as any;
            const endTimeValue = updates.end_time as any;
            updateData.start_time_1 = startTimeValue instanceof Date 
              ? startTimeValue.toISOString()
              : (typeof startTimeValue === 'string' ? startTimeValue : new Date(startTimeValue).toISOString());
            updateData.end_time_1 = endTimeValue instanceof Date 
              ? endTimeValue.toISOString()
              : (typeof endTimeValue === 'string' ? endTimeValue : new Date(endTimeValue).toISOString());
          }
          
          if (updates.employee_id) {
            const employee = currentState.employees.find(emp => emp.id === updates.employee_id);
            if (employee) {
              updateData = {
                ...updateData,
                employee_name: employee.name,
                snapshot_employee_name: null,
                snapshotEmployeeName: null,
                mp: employee.mp,
                isHourly: employee.isHourly,
                hourlyRate: employee.hourlyRate,
                monthlySalary: employee.monthlySalary,
                weeklyOffDays: employee.weeklyOffDays,
                weeklyWorkHours: employee.weeklyWorkHours,
                taxRate: employee.taxRate,
                dependents: employee.dependents,
                hasFourInsurance: employee.hasFourInsurance,
                payday: employee.payday,
                isweeklyWork: employee.isweeklyWork,
              };
            }
          }
          
          // 수정 전 데이터 가져오기 (히스토리 기록용)
          const oldSchedule = currentState.schedules.find(sched => sched.id === scheduleId);
          
          await updateWorkScheduleInShared(userId, scheduleId, updateData);
          
          // 실시간 리스너가 상태를 업데이트하므로 로컬 상태 업데이트는 제거
          // 로딩 상태만 업데이트
          set({ loading: false });
          
          // 히스토리 기록 (비동기, 실패해도 메인 작업은 계속)
          const updatedSchedule = { ...oldSchedule, ...updateData } as WorkSchedule;
          if (oldSchedule) {
            employeeHistoryService.recordWorkScheduleUpdated(userId, oldSchedule, updatedSchedule, { skipFirestoreSync: false })
              .catch(err => console.error('⚠️ 근무 일정 수정 히스토리 기록 실패:', err));
          }
          
        } catch (error: any) {
          console.error('❌ 근무 일정 수정 실패:', error);
          set({ error: error.message, loading: false });
        }
      },

      // 근무 일정 삭제
      deleteSchedule: async (userId: string, scheduleId: string) => {
        try {
          set({ loading: true, error: null });
          
          
          // 삭제 전 데이터 가져오기 (히스토리 기록용)
          const currentState = get();
          const schedule = currentState.schedules.find(sched => sched.id === scheduleId);
          
          await deleteWorkScheduleFromShared(userId, scheduleId);
          
          // 실시간 리스너가 상태를 업데이트하므로 로컬 상태 업데이트는 제거
          // 로딩 상태만 업데이트
          set({ loading: false });
          
          // 히스토리 기록 (비동기, 실패해도 메인 작업은 계속)
          if (schedule) {
            employeeHistoryService.recordWorkScheduleDeleted(userId, schedule, { skipFirestoreSync: false })
              .catch(err => console.error('⚠️ 근무 일정 삭제 히스토리 기록 실패:', err));
          }
          
        } catch (error: any) {
          console.error('❌ 근무 일정 삭제 실패:', error);
          set({ error: error.message, loading: false });
        }
      },

      // 선택자 함수들
      getEmployeeById: (employeeId: string) => {
        return get().employees.find(emp => emp.id === employeeId);
      },

      getSchedulesByDate: (date: Date) => {
        const dateStr = date.toISOString().split('T')[0];
        return get().schedules.filter(sched => sched.date.startsWith(dateStr));
      },

      getSchedulesByEmployee: (employeeId: string) => {
        return get().schedules.filter(sched => sched.employee_id === employeeId);
      },

      // 상태 관리 함수들
      setEmployees: (employees: Employee[]) => set({ employees }),
      setSchedules: (schedules: WorkSchedule[]) => set({ schedules }),
      setLoading: (loading: boolean) => set({ loading }),
      setError: (error: string | null) => set({ error }),
      
      // 캐시 초기화 (가계부 전환 시 사용)
      clearCache: () => {
        
        // Store 상태 초기화
        set({ 
          lastFetchKey: null,
          employees: [],
          schedules: [],
          employeesLoading: false,
          schedulesLoading: false,
          loading: false,
        });
        
        // Hook 레벨의 전역 캐시도 초기화 (동적 import로 순환 참조 방지)
        import('../hooks/useEmployeeData').then(({ clearEmployeeDataCache }) => {
          clearEmployeeDataCache();
        });
        import('../hooks/useWorkScheduleData').then(({ clearWorkScheduleDataCache }) => {
          clearWorkScheduleDataCache();
        });
        
      },
    } as EmployeeState))
  )
);
